import React, { useState } from "react"
import Icon from "../Icon"
import PayScoreGauge from "../PayScoreGauge"
import { formatMoney, formatDateTimeEAT, toNairobiDate } from "../../utils/format"
import { getGroupTheme } from "../../utils/groupTheme"
import GroupSearchPanel from "./GroupSearchPanel"
import GroupArchiveConfirmModal from "./GroupArchiveConfirmModal"

const formatHoursLeftUntil = (value, referenceNow = new Date()) => {
  const target = toNairobiDate(value)
  const now = toNairobiDate(referenceNow)
  if (!target || !now) return ""

  const diffMs = target.getTime() - now.getTime()
  if (diffMs <= 0) return "due now"
  const totalMinutes = Math.ceil(diffMs / 60000)
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  if (hours <= 0) return `${minutes}m left`
  if (minutes === 0) return `${hours}h left`
  return `${hours}h ${minutes}m left`
}

function GroupList({
  groups,
  viewMode = "active",
  onSelectGroup,
  onArchiveGroup,
  onReactivateGroup,
  onRenameGroup,
  isRenamingGroupId = "",
  isUpdatingArchiveGroupId = "",
  payScore = null,
  payScoreLoading = false,
  payScoreError = "",
  onRefreshPayScore,
  registeredName = "",
  showPayScore = true,
  showSearchPanel = true,
}) {
  const [editingGroupId, setEditingGroupId] = useState("")
  const [nameDraft, setNameDraft] = useState("")
  const [renameError, setRenameError] = useState("")
  const [archiveActionModal, setArchiveActionModal] = useState(null)
  const isArchivedView = String(viewMode || "").trim().toLowerCase() === "archived"
  const groupScore = payScore?.modules?.group || null
  const finalScore = payScore?.final || null
  const groupValue = Number(groupScore?.score)
  const finalValue = Number(finalScore?.score)
  const hasGroupScore = Number.isFinite(groupValue)
  const hasFinalScore = Number.isFinite(finalValue)
  const hasPayScoreBlock =
    showPayScore &&
    !isArchivedView &&
    (payScoreLoading || !!payScoreError || hasGroupScore || hasFinalScore || !!groupScore?.hasCurrentDefault) &&
    (Array.isArray(groups) && groups.length > 0)

  const payScoreCard = hasPayScoreBlock ? (
    <div className="bg-white/95 rounded-2xl border border-gray-200 shadow-sm p-4 flex flex-col items-center">
      <PayScoreGauge 
        score={groupValue} 
        label="Groups PaySCORE" 
        status={groupScore?.status}
      />
      <p className="mt-3 text-[10px] text-gray-500 text-center font-medium max-w-[200px]">
        This score is based on your group payments performance.
      </p>
      <p className="mt-3 text-[10px] text-gray-500 text-center font-medium">
        Over the LAST 36 Payment Cycles <br/>(Sometimes up to 84 Payment Cycles)
      </p>
    </div>
  ) : null

  const closeArchiveActionModal = () => setArchiveActionModal(null)

  const handleConfirmArchiveAction = async () => {
    const actionGroupId = String(archiveActionModal?.groupId || "").trim()
    if (!actionGroupId) {
      return { ok: false, message: "Group not found." }
    }

    if (archiveActionModal?.mode === "archive") {
      return onArchiveGroup?.(actionGroupId) || { ok: false, message: "Archive is unavailable." }
    }

    return onReactivateGroup?.(actionGroupId) || { ok: false, message: "Reactivate is unavailable." }
  }

  if (!groups || groups.length === 0) {
    return (
      <div className="space-y-3 sm:space-y-4">
        {payScoreCard}
        {showSearchPanel && <GroupSearchPanel registeredName={registeredName} />}
        <div className="bg-white/90 rounded-2xl border border-dashed border-gray-300 p-8 text-center">
          <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gray-100 flex items-center justify-center">
            <Icon name={isArchivedView ? "archive" : "car-front"} size="1.5rem" className="text-gray-400" />
          </div>
          <h2 className="text-base font-semibold text-gray-800 mb-1">
            {isArchivedView ? "No archived groups" : "No groups yet"}
          </h2>
          <p className="text-xs text-gray-500 mb-3">
            {isArchivedView
              ? "Groups you archive will appear here."
              : "Create a group, invite friends, and start collecting contributions."}
          </p>
          {!isArchivedView && (
            <p className="text-xs text-gray-400">
              (Use the <span className="font-semibold text-gray-600">New</span> button above to get started.)
            </p>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-3 sm:space-y-4">
      {payScoreCard}
      {showSearchPanel && <GroupSearchPanel registeredName={registeredName} />}
      <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2">
        {groups.map((group) => {
          const members = Array.isArray(group?.members) ? group.members : []
          const membersCount = Number(group?.membersCount || 0) || members.length
          const isSavingsGroup =
            !!group?.isChamaa ||
            !!group?.isProjectSavings ||
            String(group?.type || "").toLowerCase().includes("savings")
          const myMemberEntry =
            members.find((member) => String(member?.id || "") === String(group?.myMemberId || "")) || null
          const totalExpected =
            Number.isFinite(Number(group?.summaryTotalExpected))
              ? Number(group.summaryTotalExpected)
              : members.length > 0
              ? members.reduce((sum, m) => sum + (m.expected || 0), 0)
              : group.totalBudget || 0
          const totalPaid =
            Number.isFinite(Number(group?.summaryTotalPaid))
              ? Number(group.summaryTotalPaid)
              : members.length > 0
              ? members.reduce((sum, m) => sum + (m.paid || 0), 0)
              : group.groupWalletBalance || 0
          const percentage =
            totalExpected > 0 ? Math.min(100, Math.round((totalPaid / totalExpected) * 100)) : 0
          const myExpected = Number(myMemberEntry?.expected || 0)
          const myPaid = Number(myMemberEntry?.paid || 0)
          const myBalanceCandidate = Number.isFinite(Number(group?.summaryMyBalance))
            ? Number(group.summaryMyBalance)
            : (myMemberEntry?.dueAmount ?? myMemberEntry?.balanceAmount ?? (myExpected - myPaid))
          const myBalance = Math.max(
            0,
            Number(myBalanceCandidate || 0)
          )
          const myHasOutstandingDebt =
            group?.summaryMyHasOutstandingDebt === undefined
              ? !!myMemberEntry?.hasOutstandingDebt
              : !!group.summaryMyHasOutstandingDebt

          const isAdmin = String(group?.myRole || "").toLowerCase() === "admin"
          const canEditName = isAdmin && !isArchivedView
          const isEditing = String(editingGroupId) === String(group.id)
          const isRenaming = String(isRenamingGroupId || "") === String(group.id)
          const isUpdatingArchiveState = String(isUpdatingArchiveGroupId || "") === String(group.id)
          const isLate =
            (isSavingsGroup && myBalance > 0) ||
            myHasOutstandingDebt
          const isChamaa = !!group?.isChamaa
          const theme = getGroupTheme(group)
          const cardBgClass = isChamaa ? "bg-[#53F587]/20" : theme.cardBg

          const frequencyLabel =
            group.activeCycle?.invoiceFrequency ||
            group.frequency ||
            group.invoiceFrequency ||
            group.contribution?.longTerm?.frequency ||
            null

          const nextDueDate = (() => {
            const activeCycle = group.activeCycle || null
            const orderedRounds = Array.isArray(activeCycle?.rounds)
              ? [...activeCycle.rounds].sort(
                (a, b) => Number(a?.roundNumber || 0) - Number(b?.roundNumber || 0)
              )
              : []
            const nextScheduledRound = orderedRounds.find((round) => round?.status === "scheduled") || null
            if (nextScheduledRound?.invoiceScheduledAt) return nextScheduledRound.invoiceScheduledAt

            const currentIssuedRound =
              [...orderedRounds].reverse().find((round) => round?.status === "invoice_issued") || null
            if (!currentIssuedRound) return null
            return (
              currentIssuedRound?.payoutScheduledAt ||
              currentIssuedRound?.invoiceDueDate ||
              currentIssuedRound?.invoiceScheduledAt ||
              null
            )
          })()
          const nextDueCountdownLabel = nextDueDate ? formatHoursLeftUntil(nextDueDate) : ""
          const nextDueDateTime = nextDueDate ? formatDateTimeEAT(nextDueDate) : "TBC"

          return (
            <div
              key={group.id}
              className={`text-left rounded-2xl border border-gray-200 shadow-sm hover:shadow-lg active:shadow-md transition-all hover:scale-[1.01] active:scale-[0.99] p-4 sm:p-5 border-l-4 ${theme.borderLeft} ${cardBgClass}`}
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex-1 min-w-0">
                  {isEditing ? (
                    <div className="space-y-1.5">
                      <input
                        type="text"
                        value={nameDraft}
                        onChange={(e) => setNameDraft(e.target.value)}
                        className={`w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 ${theme.ring}`}
                        placeholder="Group name"
                        autoFocus
                      />
                      {!!renameError && (
                        <p className="text-[10px] text-red-600">{renameError}</p>
                      )}
                      <div className="flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={async () => {
                            const nextName = String(nameDraft || "").trim()
                            if (!nextName) {
                              setRenameError("Group name is required.")
                              return
                            }
                            const result = await onRenameGroup?.(group.id, nextName)
                            if (!result?.ok) {
                              setRenameError(result?.message || "Failed to rename group.")
                              return
                            }
                            setEditingGroupId("")
                            setNameDraft("")
                            setRenameError("")
                          }}
                          disabled={isRenaming}
                          className={`px-2.5 py-1 rounded-md text-[11px] font-semibold transition-all disabled:opacity-60 ${theme.button}`}
                        >
                          {isRenaming ? "Saving..." : "Save"}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setEditingGroupId("")
                            setNameDraft("")
                            setRenameError("")
                          }}
                          className="px-2.5 py-1 rounded-md text-[11px] font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start gap-1.5 min-w-0">
                      <button
                        type="button"
                        onClick={() => onSelectGroup?.(group.id)}
                        className="text-left min-w-0"
                      >
                        <h2 className={`text-sm sm:text-base font-semibold text-gray-900 mb-1 line-clamp-2 hover:${theme.text} transition-colors`}>
                          {group.name}
                        </h2>
                      </button>
                      <div className="flex items-center gap-0.5 flex-shrink-0">
                        {canEditName && (
                          <button
                            type="button"
                            title="Edit group name"
                            onClick={() => {
                              setEditingGroupId(String(group.id))
                              setNameDraft(String(group?.name || ""))
                              setRenameError("")
                            }}
                            className="inline-flex h-7 w-7 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="w-3.5 h-3.5">
                              <path d="M13.488 2.513a1.75 1.75 0 0 0-2.475 0L6.75 6.774a2.75 2.75 0 0 0-.706 1.224l-.395 1.58a.75.75 0 0 0 .92.92l1.58-.395a2.75 2.75 0 0 0 1.225-.707l4.262-4.261a1.75 1.75 0 0 0 0-2.475Zm-1.414 1.06a.25.25 0 0 1 .354 0l1 1a.25.25 0 0 1 0 .354L9.25 9.114a1.25 1.25 0 0 1-.556.322l-.885.221.222-.885a1.25 1.25 0 0 1 .321-.557l4.722-4.642Z" />
                              <path d="M2 3.5A1.5 1.5 0 0 1 3.5 2h4a.75.75 0 0 1 0 1.5h-4a.75.75 0 0 0-.75.75v8.5c0 .414.336.75.75.75h8.5a.75.75 0 0 0 .75-.75v-4a.75.75 0 0 1 1.5 0v4A2.25 2.25 0 0 1 12 15H3.5A2.25 2.25 0 0 1 2 12.75V3.5Z" />
                            </svg>
                          </button>
                        )}
                        {!isArchivedView ? (
                          <button
                            type="button"
                            title="Archive group"
                            onClick={() => {
                              setArchiveActionModal({
                                mode: "archive",
                                groupId: group.id,
                                groupName: group.name,
                              })
                            }}
                            disabled={isUpdatingArchiveState}
                            className="inline-flex h-7 w-7 items-center justify-center rounded-full text-orange-400 transition-colors hover:bg-orange-50 hover:text-orange-600 disabled:opacity-60"
                          >
                            <Icon name="archive" size="0.9rem" />
                          </button>
                        ) : (
                          <button
                            type="button"
                            title="Reactivate group"
                            onClick={() => {
                              setArchiveActionModal({
                                mode: "reactivate",
                                groupId: group.id,
                                groupName: group.name,
                              })
                            }}
                            disabled={isUpdatingArchiveState}
                            className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 transition-colors hover:bg-emerald-100 hover:text-emerald-800 disabled:opacity-60 shadow-sm"
                          >
                            <Icon name="arrow-repeat" size="1rem" />
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                  <p className="text-xs text-gray-500">
                    Admin: <span className="font-medium text-gray-700">{group.adminName}</span>
                  </p>
                  <p className="text-[11px] text-gray-400 mt-0.5">
                    Group ID: <span className="font-bold text-gray-600">{group.groupCode || "-"}</span>
                  </p>
                  <p className="text-[11px] text-gray-400 mt-0.5">
                    Type: <span className="font-bold uppercase text-gray-600">{group.type || "-"}</span>
                  </p>
                  {frequencyLabel && (
                    <p className="text-[11px] text-gray-400 mt-0.5">
                      Frequency: <span className="font-medium text-gray-600 uppercase">{frequencyLabel}</span>
                    </p>
                  )}
                  {isChamaa && (
                    <p className="text-[10px] text-gray-400 mt-0.5 italic">
                      Next Due (EAT):{" "}
                      <span className="font-semibold text-gray-500">
                        {nextDueDateTime}
                        {nextDueCountdownLabel ? ` (${nextDueCountdownLabel})` : ""}
                      </span>
                    </p>
                  )}
                </div>
                <div className="flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => onSelectGroup?.(group.id)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wide transition-colors ${theme.button}`}
                    aria-label={`Open ${group?.name || "group"}`}
                  >
                    Open
                    <Icon name="arrow-right" size="0.75rem" />
                  </button>
                </div>
              </div>

              <button type="button" onClick={() => onSelectGroup?.(group.id)} className="w-full text-left">
                <div className="grid grid-cols-2 gap-3 mb-3">
                  {/* Budget box — unified themed design for all group types */}
                  <div className={`rounded-xl px-3 py-2.5 ${theme.mutedBg} border ${theme.border}`}>
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <span className={`w-5 h-5 rounded-md ${theme.progressBg} flex items-center justify-center flex-shrink-0`}>
                        <Icon name="wallet2" size="0.65rem" className={theme.text} />
                      </span>
                      <p className={`text-[10px] font-semibold uppercase tracking-wide ${theme.mutedText}`}>Budget</p>
                    </div>
                    <p className={`text-xs sm:text-sm font-bold truncate ${theme.accentText}`}>
                      {formatMoney(group.totalBudget || 0)}
                    </p>
                    <p className={`text-[10px] mt-0.5 truncate ${theme.mutedText}`}>
                      {formatMoney(group.amountPerPerson || 0)} / person
                    </p>
                  </div>

                  {/* Collected / My balance box — unified themed design for all group types */}
                  <div className={`rounded-xl px-3 py-2.5 border-t-2 ${
                    isLate
                      ? "bg-red-50 border-2 border-red-200 animate-pulse"
                      : `${theme.bg} ${theme.border} ${theme.borderLeft}`
                  }`}>
                    {isSavingsGroup ? (
                      <>
                        <div className="flex items-center gap-1.5 mb-1.5">
                          <span className={`w-5 h-5 rounded-md ${theme.progressBg} flex items-center justify-center flex-shrink-0`}>
                            <Icon name="piggy-bank" size="0.65rem" className={isLate ? "text-red-600" : theme.text} />
                          </span>
                          <p className={`text-[10px] font-semibold uppercase tracking-wide ${isLate ? "text-red-700" : theme.mutedText}`}>My balance</p>
                        </div>
                        <p className={`text-xs sm:text-sm font-black truncate ${isLate ? "text-red-600" : "text-rose-600"}`}>
                          {formatMoney(myBalance)}
                        </p>
                        <p className={`text-[10px] mt-0.5 ${isLate ? "text-red-600 font-bold" : theme.mutedText}`}>
                          {isLate ? "OVERDUE BALANCE" : myBalance > 0 ? "Amount you still owe" : "No outstanding balance"}
                        </p>
                      </>
                    ) : (
                      <>
                        <div className="flex items-center gap-1.5 mb-1.5">
                          <span className={`w-5 h-5 rounded-md ${theme.progressBg} flex items-center justify-center flex-shrink-0`}>
                            <Icon name="cash-coin" size="0.65rem" className={theme.text} />
                          </span>
                          <p className={`text-[10px] font-semibold uppercase tracking-wide ${theme.mutedText}`}>Collected</p>
                        </div>
                        <p className={`text-xs sm:text-sm font-bold truncate ${theme.text}`}>
                          {formatMoney(totalPaid)}
                        </p>
                        <p className={`text-[10px] mt-0.5 ${theme.mutedText}`}>
                          {percentage}% of {formatMoney(totalExpected)}
                        </p>
                      </>
                    )}
                  </div>
                </div>

                <div className={`h-2 sm:h-2.5 w-full ${theme.progressBg} rounded-full overflow-hidden mb-3`}>
                  <div
                    className={`h-full ${theme.progressFill} transition-all duration-300`}
                    style={{ width: `${percentage}%` }}
                  />
                </div>

                <div className="flex items-center justify-between text-[11px] sm:text-xs text-gray-500">
                  <span className="inline-flex items-center gap-1.5">
                    <Icon name="people" size="0.9rem" />
                    <span className="font-medium">{membersCount}</span> members
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <Icon name="wallet" size="0.9rem" />
                    <span className="font-semibold text-gray-800">
                      {formatMoney(group.groupWalletBalance || 0)}
                    </span>
                  </span>
                </div>
              </button>
            </div>
          )
        })}
      </div>
      <GroupArchiveConfirmModal
        open={!!archiveActionModal}
        mode={archiveActionModal?.mode || "archive"}
        groupName={archiveActionModal?.groupName || ""}
        isSubmitting={
          !!archiveActionModal &&
          String(isUpdatingArchiveGroupId || "") === String(archiveActionModal?.groupId || "")
        }
        onClose={closeArchiveActionModal}
        onConfirm={handleConfirmArchiveAction}
      />
    </div>
  )
}

export default GroupList
